/**
 * PipelineQuickCreateDialog - Minimal dialog for quick pipeline creation
 *
 * Following the Quick Create Dialog pattern:
 * - Customer type (B2B/B2C) is picked FIRST; it drives a sensible default
 *   Pipeline Name (e.g. "B2B Sales Pipeline") and seeds the funnel
 *   (usePipelineDefaults) so the new pipeline is immediately usable. The
 *   suggested name only auto-updates while the user hasn't typed their own.
 * - Returns the created pipeline (with stages) via onCreated so the caller
 *   (PipelinePicker) can auto-select it and land a deal in its first stage.
 *
 * Full stage editing (add/remove/reorder, gate criteria) lives on the
 * Pipelines page — this dialog only covers the create-in-context case.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { GitBranch } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { DialogForm } from '../../../shared/components/common';
import { useCreatePipeline, usePipelineDefaults } from '../hooks/usePipelines';
import { useApiError } from '../../../shared/hooks/useApiError';
import type { CustomerSegment, Pipeline } from '../types';

interface PipelineQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fill the name field (e.g., from picker search text) */
  defaultName?: string;
  /** Callback when the pipeline is created */
  onCreated?: (pipeline: Pipeline) => void;
}

interface QuickCreateFormData {
  name: string;
  customer_segment: CustomerSegment;
}

export function PipelineQuickCreateDialog({
  open,
  onClose,
  defaultName = '',
  onCreated,
}: PipelineQuickCreateDialogProps) {
  const { t } = useTranslation(['pipeline', 'common']);
  const createMutation = useCreatePipeline();
  const { handleMutationError } = useApiError({ showToast: false });
  // Tenant-derived default tells us which segment to pre-select.
  const { data: tenantDefault } = usePipelineDefaults();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    mode: 'onBlur',
    defaultValues: { name: defaultName, customer_segment: 'b2b' },
  });

  // Stages/description are seeded from the *selected* segment's preset, so the
  // funnel matches the segment the user picked rather than the tenant default.
  const selectedSegment = watch('customer_segment');
  const { data: segmentDefault, isFetching: segmentLoading } =
    usePipelineDefaults(selectedSegment);

  // Suggested default name per segment (e.g. "B2B Sales Pipeline").
  const defaultNameForSegment = useCallback(
    (seg: CustomerSegment) =>
      seg === 'b2c'
        ? t('pipelineQuickCreate.defaultNameB2c')
        : t('pipelineQuickCreate.defaultNameB2b'),
    [t],
  );

  // Once the user types their own name, stop auto-filling it on segment change.
  const nameEditedRef = useRef(false);

  // Initialise the form once per open, after the primary segment is known.
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      nameEditedRef.current = false;
      return;
    }
    if (initializedRef.current || !tenantDefault) return;
    initializedRef.current = true;
    const seg = tenantDefault.customer_segment ?? 'b2b';
    // A name passed in from the picker is a deliberate value — preserve it and
    // don't let a later segment switch overwrite it.
    nameEditedRef.current = !!defaultName;
    reset({
      name: defaultName || defaultNameForSegment(seg),
      customer_segment: seg,
    });
  }, [open, defaultName, tenantDefault, reset, defaultNameForSegment]);

  const onSubmit = async (data: QuickCreateFormData) => {
    const stages = (segmentDefault?.stages ?? []).map((s) => ({
      name: s.name,
      stage_type: s.stage_type ?? 'open',
      probability:
        s.stage_type === 'won' ? 100 : s.stage_type === 'lost' ? 0 : s.probability ?? 50,
      color: s.color,
    }));
    try {
      const pipeline = await createMutation.mutateAsync({
        name: data.name.trim(),
        description: segmentDefault?.description || undefined,
        customer_segment: data.customer_segment,
        stages,
      });
      onCreated?.(pipeline);
      onClose();
    } catch (error) {
      handleMutationError(error, setError);
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('pipelineQuickCreate.title')}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={t('common:app.create')}
      cancelLabel={t('common:app.cancel')}
      // Block submit until the selected segment's stages have loaded, so we never
      // create a pipeline with an empty funnel mid-fetch.
      isLoading={isSubmitting || segmentLoading}
      maxWidth="sm"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>{t('pipelineQuickCreate.segmentLabel')}</Label>
          <Controller
            name="customer_segment"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  // Refresh the suggested name to match — unless the user has
                  // already typed their own.
                  if (!nameEditedRef.current) {
                    setValue('name', defaultNameForSegment(value as CustomerSegment));
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="b2b">{t('pipelineQuickCreate.segmentB2b')}</SelectItem>
                  <SelectItem value="b2c">{t('pipelineQuickCreate.segmentB2c')}</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="pipeline-name">{t('pipelineQuickCreate.nameLabel')} *</Label>
          <Controller
            name="name"
            control={control}
            rules={{ required: t('common:validation.required') }}
            render={({ field }) => (
              <Input
                {...field}
                onChange={(e) => {
                  nameEditedRef.current = true;
                  field.onChange(e);
                }}
                id="pipeline-name"
                autoFocus
                placeholder={t('pipelineQuickCreate.namePlaceholder')}
                className={cn(errors.name && 'border-destructive')}
              />
            )}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name.message}</p>
          )}
        </div>

        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <GitBranch className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {t('pipelineQuickCreate.hint')}
        </p>
      </div>
    </DialogForm>
  );
}
